import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";

const KIND_TITLES = {
  users: { title: "Users", cols: ["email", "full_name", "role", "account_status", "created_at"] },
  items: { title: "Items master", cols: ["sku", "name", "item_type", "vendor_name", "standard_cost", "sale_price", "hsn_code"] },
  warehouses: { title: "Warehouses", cols: ["code", "name", "address", "is_default"] },
  purchase_orders: { title: "Purchase orders", cols: ["number", "title", "vendor_name", "total", "status"] },
  requisitions: { title: "Requisitions", cols: ["number", "title", "department", "estimated_total", "status"] },
  grns: { title: "GRNs", cols: ["number", "vendor_name", "received_date", "status"] },
  sales_invoices: { title: "Sales invoices", cols: ["number", "customer_name", "total", "outstanding", "status"] },
  payments: { title: "Payments", cols: ["number", "customer_name", "method", "amount", "date"] },
  credit_notes: { title: "Credit notes", cols: ["number", "customer_name", "amount", "status"] },
  production_orders: { title: "Production orders", cols: ["number", "product_name", "quantity", "current_stage", "status"] },
  qc_inspections: { title: "QC inspections", cols: ["number", "inspection_type", "quantity_inspected", "rejected_quantity", "overall_result"] },
  audit_events: { title: "Audit events", cols: ["action", "entity_type", "entity_id", "actor_email", "created_at"] },
  vendors: { title: "Vendors", cols: ["name", "category", "contact_email", "contact_phone", "gstin"] },
  dispatches: { title: "Dispatches", cols: ["number", "customer_name", "vehicle_no", "total_sqm", "total_value", "status"] },
  vendor_invoices: { title: "Vendor invoices", cols: ["number", "vendor_name", "invoice_date", "due_date", "total", "status"] },
};

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string" && v.length === 36 && v.includes("-")) return v.slice(0, 8);
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return new Date(v).toLocaleString();
  return String(v);
}

export default function OrgAuditDetail() {
  const { kind } = useParams();
  const meta = KIND_TITLES[kind];
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get(`/org/audit-detail/${kind}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [kind]);

  if (!meta) return <div className="p-4 sm:p-6 lg:p-8" data-testid="audit-detail-page"><div className="surface-card p-8 text-center text-muted-foreground">Unknown audit kind: {kind}</div></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid={`audit-detail-${kind}-page`}>
      <div className="flex items-center gap-3">
        <Link to="/org/audit-ca"><Badge variant="outline" className="cursor-pointer"><ArrowLeft className="w-3 h-3 mr-1" /> Back to CA Audit</Badge></Link>
      </div>
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">{meta.title}</h1>
        <p className="text-muted-foreground">{rows.length} record{rows.length === 1 ? "" : "s"} — read-only auditor view.</p>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {meta.cols.map((c) => <TableHead key={c}>{c.replace(/_/g, " ")}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={r.id || idx} data-testid={`audit-detail-row-${idx}`}>
                {meta.cols.map((c) => <TableCell key={c} className="text-xs font-mono">{fmt(r[c])}</TableCell>)}
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={meta.cols.length} className="text-center text-muted-foreground py-8">No records.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
